import React, { useState, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import logo from '../assets/store-space-self-storage-logo.png';
import { selectCurrentUser, logout } from '../store/authSlice';
import { brandColors, drawerPaperSx, sidebarColors } from '../styles/brandColors';

export const DRAWER_WIDTH = 260;

const navItems = [
  { label: 'Dashboard', path: '/dashboard', icon: DashboardOutlinedIcon },
  { label: 'Claims', path: '/claims', icon: AssignmentOutlinedIcon, matchPrefix: true },
  { label: 'Facilities', path: '/facilities', icon: BusinessOutlinedIcon },
  { label: 'Settings', path: '/settings', icon: SettingsOutlinedIcon },
];

const pageTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/claims': 'Claims',
  '/facilities': 'Facilities',
  '/settings': 'Settings',
};

const getPageTitle = (pathname: string): string => {
  if (pathname.startsWith('/claims/')) return 'Claim Details';
  if (pathname.includes('/edit')) return 'Edit Claim';
  return pageTitles[pathname] || 'Admin';
};

const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname]);

  const isNavActive = (path: string, matchPrefix?: boolean) => {
    if (matchPrefix) return location.pathname === path || location.pathname.startsWith(`${path}/`);
    return location.pathname === path;
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    setAnchorEl(null);
    navigate('/login');
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
    } else {
      setSidebarOpen((prev) => !prev);
    }
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${sidebarColors.border}` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            backgroundColor: sidebarColors.logoStrip,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Store Space Self Storage"
            sx={{ maxWidth: '100%', height: 36, objectFit: 'contain' }}
          />
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 1.5, py: 2 }}>
        {navItems.map(({ label, path, icon: Icon, matchPrefix }) => {
          const active = isNavActive(path, matchPrefix);
          return (
            <ListItemButton
              key={path}
              onClick={() => handleNavClick(path)}
              selected={active}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                color: sidebarColors.text,
                borderLeft: '3px solid transparent',
                '&.Mui-selected': {
                  backgroundColor: sidebarColors.activeBg,
                  color: sidebarColors.activeText,
                  borderLeftColor: sidebarColors.activeBorder,
                  '&:hover': { backgroundColor: 'rgba(220, 53, 69, 0.22)' },
                  '& .MuiListItemIcon-root': { color: sidebarColors.activeIcon },
                },
                '&:hover': { backgroundColor: sidebarColors.backgroundHover },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: active ? sidebarColors.activeIcon : sidebarColors.textMuted,
                }}
              >
                <Icon />
              </ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{ fontWeight: active ? 600 : 400, fontSize: '0.95rem' }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ px: 2, py: 2, borderTop: `1px solid ${sidebarColors.border}` }}>
        <Typography variant="caption" sx={{ color: sidebarColors.textMuted }}>
          Claims Admin Portal
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {!isMobile && sidebarOpen && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          width: !isMobile && sidebarOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: 'background.paper',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <IconButton
              edge="start"
              onClick={toggleSidebar}
              aria-label={sidebarOpen ? 'Hide navigation' : 'Show navigation'}
              sx={{ color: 'primary.main' }}
            >
              {(!isMobile && sidebarOpen) || (isMobile && mobileOpen) ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>

            <Typography
              variant="h5"
              component="h1"
              sx={{ flexGrow: 1, fontWeight: 600, color: brandColors.redLight }}
            >
              {pageTitle}
            </Typography>

            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: 'primary.main' }}>
              <AccountCircleOutlinedIcon sx={{ fontSize: 28, color: brandColors.red }} />
              <Typography variant="body2" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
                {user?.name || 'User'}
              </Typography>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
